import Logger from 'Common/logs/Logger';
import BrowserTransport from 'Browser/logs/BrowserTransport';
import BrowserFormat from 'Browser/logs/BrowserFormat';
import { createViteBrowserRouter, createBrowserRouter, ConnectionToServer } from 'Shared/Root';
import { hydrate, render } from 'solid-js/web';
import { Environment } from 'Common/Environment';
import { SiteBasics } from 'Common/SiteBasics';
import { User } from 'Browser/User';
import { createTRPCClient, httpBatchLink, loggerLink, TRPCClient } from '@trpc/client';
import { parse, stringify } from 'devalue';
import { browserId, BrowserId, idString, isBrowserId } from 'Browser/BrowserId';
import type { AppRouter } from 'Server/rpc/router';


export class Browser
{
	constructor(
		private env:Environment,
		private site:SiteBasics<BrowserId>,
		private user:User<BrowserId>
	)
	{}

	async run()
	{
//XXX perhaps raise the level for production   
		window.log = new Logger(undefined);
		log.add(new BrowserTransport(),new BrowserFormat(),'debug');

		const server = initTrpcClient(this.env);

		const basicData = {
			build: this.env,
			site: this.site,
			user: this.user,
			server: server,
			response: {status:200}
		};

		handleRejectedPromises(server,this.env);

		try {

			if (window.useVite) 
				render(createViteBrowserRouter(basicData), document.getElementById('root') as HTMLElement);
			else
				hydrate(createBrowserRouter(basicData), document);
		}
		catch (err) {
			/* These are "top level" errors that indicate the router has crashed or failed to start. Hopefully rare. */
			log.error(err);
			const message = err instanceof Error ? err.message : String(err);
			const stack = err instanceof Error ? err.stack ?? '' : '';
			server.logBrowserError.mutate({type:'top level',location:location.href,message:message,stack:stack});
			alert(`An error occurred: ${message}. Perhaps try refreshing the browser page.`);
		}
	}
}

function handleRejectedPromises(server:ConnectionToServer, env:Environment)
{
	let inError = false;

	window.onunhandledrejection = async (err) => {
		const message = err.reason?.message ?? String(err.reason);

		//XXX this is needed in Chrome for some reason
		log.error(message);

		if (isStaleBuild(message) && reloadForNewBuild())
			return;

		/* Its a bit too confusing sending errors back to the server when in dev */
		if (!env.logToConsole && !inError) {
			inError = true;
			await server.logBrowserError.mutate({type:'unhandledRejection',location:location.href,message:message,stack:err.reason?.stack});
			inError = false;				
			alert(`An error occurred: ${message}. Perhaps try refreshing the browser page.`);
		}
	};
}

const staleBuildKey = 'reloadedForNewBuild';

/* A deploy replaces the build, so a page loaded before it asks for route chunks that are no longer
   there. The wording is Chrome's, Firefox's and Safari's respectively. */
function isStaleBuild(message:string)
{
	return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/.test(message);
}

/* Reloading picks up the new build. Only once per page: a chunk still missing after that is
   missing for some other reason, and is reported like any other error. */
function reloadForNewBuild()
{
	try {
		if (sessionStorage.getItem(staleBuildKey)==location.href)
			return false;
		sessionStorage.setItem(staleBuildKey,location.href);
	}
	catch {
		return false;
	}

	location.reload();
	return true;
}

export function initTrpcClient(env:Environment): TRPCClient<AppRouter>
{
	const transformer = {
		deserialize: (object: any) => {
			return parse(object, {
				ObjectId: value => browserId(value)
			})
		},

		serialize: (object: any) => stringify(object,{
			Id: value => isBrowserId(value) && idString(value) /* NB returns undefined if not an id */
		})
	};

	return createTRPCClient<AppRouter>({
		links: [
			loggerLink({
				enabled: (_opts) => env.logToConsole
			}),

//			httpLink({url: '/_rpc',transformer }),    //XXX this is giving errors for some reason... probably the transformer

			/*
				WARNING: 
				Turning off batching doesn't really guarantee that operations will occur in the correct order on the server 
				as requests can still come in separately and overlap in the server - its just less likely to occur.
				Arguably its better to enable batching because we're more likely to detect problems.

				NOTE: can turn off batching in createRouter.ts (server-side) using the 'allowBatching' option.
				      Presumably 'maxItems' here would do the same thing.
					  httpLink() could be used instead but is throwing errors for some reason.
			*/			
			httpBatchLink({
				url: '/_rpc',
//				maxItems: 1,
				transformer,
			}),

		],
	});
}

